import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { PublicStatsCard } from "@/components/PublicStatsCard";
import { FunnelLeadsTab } from "@/components/FunnelLeadsTab";
import { StoriesManager } from "@/components/StoriesManager";
import { PartnersManager } from "@/components/PartnersManager";
import { IntegrationsPanel } from "@/components/IntegrationsPanel";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { STATUS_STYLES } from "@/pages/MyApplications";
import { Users, BookOpen, Briefcase, FileText, Plus, Trash2, Loader2, Shield, ListChecks, Presentation, IndianRupee, Mail, Star, GripVertical, Download, CheckCircle2, XCircle, CalendarDays, Sparkles } from "lucide-react";

const newKey = () => Math.random().toString(36).slice(2);

const STATUSES = ["Submitted", "Under Review", "Interview", "Offered", "Rejected"];
const ADMIN_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const EXPERT_STATUSES = ["New", "Reviewing", "Approved", "Rejected"];
const VISITOR_STATUSES = ["New", "Contacted", "Converted"];
const JOB_TYPES = ["Full-time", "Internship", "Remote"];
const EXPERT_STATUS_STYLES = {
  Approved: "bg-emerald-600",
  Rejected: "bg-red-500",
  Reviewing: "bg-amber-500",
};
const VISITOR_STATUS_STYLES = {
  Converted: "bg-emerald-600",
  Contacted: "bg-amber-500",
  New: "bg-slate-500",
};

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <Card className="border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-center gap-4">
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-heading text-2xl font-extrabold text-slate-900">{value ?? "—"}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </Card>
  );
}

function VisitorRow({ lead, onChanged }) {
  const { t } = useTranslation();
  const [note, setNote] = useState(lead.note || "");
  const [savingNote, setSavingNote] = useState(false);
  const dirty = note !== (lead.note || "");

  const patch = async (body, msg) => {
    await api.patch(`/admin/visitors/${lead.lead_id}`, body);
    toast.success(msg);
    onChanged();
  };

  const saveNote = async () => {
    setSavingNote(true);
    try {
      await patch({ note }, t("admin.noteSaved"));
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <Card data-testid="admin-visitor-row" className="border-slate-200 p-4">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-900">{lead.name}</p>
            <span className="text-xs text-slate-400" data-testid="visitor-date">{new Date(lead.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-xs text-slate-500">{lead.email}{lead.phone ? ` • ${lead.phone}` : ""}</p>
          {lead.interest && (
            <p className="mt-1 text-sm text-slate-600"><span className="font-medium">{t("admin.vInterest")}</span> {lead.interest}</p>
          )}
          {lead.message && <p className="mt-1 text-xs text-slate-400 italic">"{lead.message}"</p>}
          <div className="mt-3 flex items-start gap-2">
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("admin.notePlaceholder")}
              rows={2}
              className="text-sm"
              data-testid="visitor-note-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={saveNote}
              disabled={savingNote || !dirty}
              data-testid="visitor-note-save"
            >
              {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : t("admin.saveNote")}
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={VISITOR_STATUS_STYLES[lead.status] || "bg-slate-500"}>{t(`visitorStatus.${lead.status}`)}</Badge>
          <Select value={lead.status} onValueChange={(val) => patch({ status: val }, t("admin.statusUpdated"))}>
            <SelectTrigger className="w-36" data-testid="visitor-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VISITOR_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{t(`visitorStatus.${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <a href={`mailto:${lead.email}`} className="text-xs font-medium text-blue-600 hover:underline" data-testid="visitor-reply-link">
            {t("admin.vReply")}
          </a>
        </div>
      </div>
    </Card>
  );
}

function FeaturedOrderManager({ courses, onSaved }) {
  const { t } = useTranslation();
  const [order, setOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const featured = courses
      .filter((c) => c.featured)
      .sort((a, b) => (a.featured_order ?? 0) - (b.featured_order ?? 0));
    setOrder(featured);
  }, [courses]);

  if (order.length < 2) return null;

  const persist = async (list) => {
    setSaving(true);
    try {
      await api.put("/admin/courses/featured-order", { order: list.map((c) => c.course_id) });
      toast.success(t("admin.orderSaved"));
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setOrder(next);
    persist(next);
  };

  return (
    <Card className="border-slate-200 p-5 mb-4" data-testid="featured-order-manager">
      <div className="flex items-center gap-2 mb-1">
        <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
        <h3 className="font-heading font-bold text-slate-900">{t("admin.featuredOrderTitle")}</h3>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
      </div>
      <p className="text-xs text-slate-500 mb-4">{t("admin.featuredOrderHint")}</p>
      <div className="space-y-2">
        {order.map((c, i) => (
          <div
            key={c.course_id}
            data-testid="featured-order-item"
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { move(dragIdx, i); setDragIdx(null); }}
            className={`flex items-center gap-3 rounded-lg border bg-white px-3 py-2.5 ${dragIdx === i ? "border-blue-400 opacity-60" : "border-slate-200"}`}
          >
            <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-slate-400" />
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-700">{i + 1}</span>
            <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-800">{c.title}</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0 || saving} onClick={() => move(i, i - 1)} data-testid={`featured-up-${i}`} title="Move up">↑</Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === order.length - 1 || saving} onClick={() => move(i, i + 1)} data-testid={`featured-down-${i}`} title="Move down">↓</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Admin() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [experts, setExperts] = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [matchDialog, setMatchDialog] = useState(null);
  const [matchList, setMatchList] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [shortlisted, setShortlisted] = useState([]);

  const viewMatches = async (req) => {
    setMatchDialog(req);
    setMatchLoading(true);
    setMatchList([]);
    setShortlisted([]);
    try {
      const { data } = await api.get(`/admin/requirements/${req.req_id}/matches`);
      setMatchList(data);
    } catch (e) {
      toast.error(t("admin.matchError"));
    } finally {
      setMatchLoading(false);
    }
  };

  const shortlistStudent = async (userId) => {
    try {
      await api.post(`/admin/requirements/${matchDialog.req_id}/shortlist`, { user_id: userId });
      setShortlisted((prev) => [...prev, userId]);
      toast.success(t("admin.shortlisted"));
    } catch (e) {
      toast.error(t("admin.shortlistError"));
    }
  };

  const loadAll = async () => {
    const [s, c, j, a, u, ex, v, rq] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/courses"),
      api.get("/jobs", { params: { type: "All" } }),
      api.get("/admin/applications"),
      api.get("/admin/users"),
      api.get("/admin/experts"),
      api.get("/admin/visitors"),
      api.get("/admin/requirements"),
    ]);
    setStats(s.data);
    setCourses(c.data);
    setJobs(j.data);
    setApps(a.data);
    setUsers(u.data);
    setExperts(ex.data);
    setVisitors(v.data);
    setRequirements(rq.data);
  };

  const exportLeadsCsv = () => {
    const headers = ["Name", "Email", "Phone", "Interest", "Message", "Status", "Note", "Date"];
    const esc = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
    const rows = visitors.map((v) =>
      [v.name, v.email, v.phone, v.interest, v.message, v.status, v.note, new Date(v.created_at).toLocaleString()].map(esc).join(",")
    );
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  useEffect(() => {
    loadAll();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10" data-testid="admin-dashboard">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-6 w-6 text-indigo-600" />
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">{t("admin.title")}</h1>
        </div>
        <p className="text-slate-500 mb-8">{t("admin.subtitle")}</p>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          <StatCard icon={Users} label={t("admin.statUsers")} testId="stat-users" value={stats?.users} color="bg-slate-800" />
          <StatCard icon={BookOpen} label={t("admin.statCourses")} testId="stat-courses" value={stats?.courses} color="bg-blue-600" />
          <StatCard icon={Briefcase} label={t("admin.statJobs")} testId="stat-jobs" value={stats?.jobs} color="bg-amber-600" />
          <StatCard icon={FileText} label={t("admin.statApplications")} testId="stat-applications" value={stats?.applications} color="bg-emerald-600" />
          <StatCard icon={Presentation} label={t("admin.statExperts")} testId="stat-experts" value={stats?.experts} color="bg-indigo-600" />
          <StatCard icon={Mail} label={t("admin.statVisitors")} testId="stat-visitors" value={stats?.visitors} color="bg-rose-600" />
        </div>

        <PublicStatsCard />

        <Tabs defaultValue="courses">
          <TabsList data-testid="admin-tabs" className="w-full justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger value="courses" data-testid="tab-courses">{t("admin.tabCourses")}</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">{t("admin.tabJobs")}</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">{t("admin.tabApplications")}</TabsTrigger>
            <TabsTrigger value="experts" data-testid="tab-experts">{t("admin.tabExperts")}</TabsTrigger>
            <TabsTrigger value="visitors" data-testid="tab-visitors">{t("admin.tabVisitors")}</TabsTrigger>
            <TabsTrigger value="requirements" data-testid="tab-requirements">{t("admin.tabRequirements")}</TabsTrigger>
            <TabsTrigger value="funnels" data-testid="tab-funnels">{t("admin.tabFunnels")}</TabsTrigger>
            <TabsTrigger value="stories" data-testid="tab-stories">{t("admin.tabStories")}</TabsTrigger>
            <TabsTrigger value="partners" data-testid="tab-partners">{t("partners.tab")}</TabsTrigger>
            <TabsTrigger value="integrations" data-testid="tab-integrations">{t("admin.tabIntegrations", "Integrations")}</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">{t("admin.tabUsers")}</TabsTrigger>
          </TabsList>

          {/* Courses */}
          <TabsContent value="courses" className="mt-6">
            <div className="flex justify-end mb-4">
              <CourseForm onCreated={loadAll} />
            </div>
            <FeaturedOrderManager courses={courses} onSaved={loadAll} />
            <div className="space-y-2">
              {courses.map((c) => (
                <Card key={c.course_id} data-testid="admin-course-row" className="border-slate-200 p-4 flex flex-wrap items-center gap-3">
                  <img src={c.thumbnail} alt="" className="h-12 w-16 rounded object-cover" />
                  <div className="flex-1 min-w-[140px]">
                    <p className="font-semibold text-slate-900 break-words">{c.title}</p>
                    <p className="text-xs text-slate-500">{c.category} • {c.lesson_count} {t("common.lessons")}{c.has_quiz ? " • quiz" : ""}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-auto">
                  <QuizForm course={c} onSaved={loadAll} />
                  <Button
                    variant="ghost"
                    size="icon"
                    className={c.featured ? "text-amber-500 hover:bg-amber-50" : "text-slate-400 hover:text-amber-500 hover:bg-amber-50"}
                    data-testid="toggle-featured-button"
                    title={c.featured ? t("admin.unfeature") : t("admin.feature")}
                    onClick={async () => {
                      await api.patch(`/admin/courses/${c.course_id}/featured`, { featured: !c.featured });
                      toast.success(c.featured ? t("admin.unfeatured") : t("admin.featured"));
                      loadAll();
                    }}
                  >
                    <Star className={`h-4 w-4 ${c.featured ? "fill-amber-500" : ""}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="delete-course-button"
                    onClick={async () => {
                      await api.delete(`/admin/courses/${c.course_id}`);
                      toast.success(t("admin.courseDeleted"));
                      loadAll();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Jobs */}
          <TabsContent value="jobs" className="mt-6">
            <div className="flex justify-end mb-4">
              <JobForm onCreated={loadAll} />
            </div>
            <div className="space-y-2">
              {jobs.map((j) => (
                <Card key={j.job_id} data-testid="admin-job-row" className="border-slate-200 p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[140px]">
                    <p className="font-semibold text-slate-900 break-words">{j.title}</p>
                    <p className="text-xs text-slate-500">{j.company} • {j.location} • {j.type}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    data-testid="delete-job-button"
                    onClick={async () => {
                      await api.delete(`/admin/jobs/${j.job_id}`);
                      toast.success(t("admin.jobDeleted"));
                      loadAll();
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Applications */}
          <TabsContent value="applications" className="mt-6">
            {apps.length === 0 ? (
              <p className="text-slate-400 py-10 text-center">{t("admin.noApplications")}</p>
            ) : (
              <div className="space-y-2">
                {apps.map((a) => (
                  <Card key={a.application_id} data-testid="admin-application-row" className="border-slate-200 p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">{a.applicant_name}</p>
                        <p className="text-xs text-slate-500">{a.applicant_email}</p>
                        <p className="mt-1 text-sm text-slate-600">
                          {t("admin.appliedTo")} <span className="font-medium">{a.job?.title}</span> @ {a.job?.company}
                        </p>
                        {a.cover_note && <p className="mt-1 text-xs text-slate-400 italic">"{a.cover_note}"</p>}
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
                          {a.resume_path && (
                            <a href={`${ADMIN_BACKEND}/api/applications/${a.application_id}/resume`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:underline" data-testid={`admin-download-resume-${a.application_id}`}>
                              <FileText className="h-3 w-3" /> {t("admin.downloadResume")}
                            </a>
                          )}
                          {a.resume_link && (
                            <a href={a.resume_link} target="_blank" rel="noreferrer" className="inline-block text-xs text-amber-700 hover:underline">
                              {t("admin.viewResume")}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={STATUS_STYLES[a.status]}>{a.status}</Badge>
                        <Select
                          value={a.status}
                          onValueChange={async (v) => {
                            await api.patch(`/admin/applications/${a.application_id}`, { status: v });
                            toast.success(t("admin.statusUpdated"));
                            loadAll();
                          }}
                        >
                          <SelectTrigger className="w-40" data-testid="application-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Experts */}
          <TabsContent value="experts" className="mt-6">
            {experts.length === 0 ? (
              <p className="text-slate-400 py-10 text-center">{t("admin.noExperts")}</p>
            ) : (
              <div className="space-y-2">
                {experts.map((x) => (
                  <Card key={x.expert_id} data-testid="admin-expert-row" className="border-slate-200 p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{x.name}</p>
                          <span className="inline-flex items-center gap-0.5 rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                            <IndianRupee className="h-3 w-3" />{x.hourly_rate}/hr
                          </span>
                        </div>
                        <p className="text-xs text-slate-500">{x.email} • {x.experience_years} {t("admin.yrsExp")}</p>
                        <p className="mt-1 text-sm text-slate-600"><span className="font-medium">{t("admin.expertise")}</span> {x.expertise}</p>
                        {x.bio && <p className="mt-1 text-xs text-slate-400 italic">"{x.bio}"</p>}
                        {x.linkedin && (
                          <a href={x.linkedin} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-blue-600 hover:underline">
                            {t("admin.viewProfile")}
                          </a>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={EXPERT_STATUS_STYLES[x.status] || "bg-slate-500"}>
                          {x.status}
                        </Badge>
                        <Select
                          value={x.status}
                          onValueChange={async (v) => {
                            await api.patch(`/admin/experts/${x.expert_id}`, { status: v });
                            toast.success(t("admin.statusUpdated"));
                            loadAll();
                          }}
                        >
                          <SelectTrigger className="w-36" data-testid="expert-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {EXPERT_STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Visitors */}
          <TabsContent value="visitors" className="mt-6">
            {visitors.length === 0 ? (
              <p className="text-slate-400 py-10 text-center">{t("admin.noVisitors")}</p>
            ) : (
              <>
                <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="leads-funnel">
                  {VISITOR_STATUSES.map((s) => {
                    const count = visitors.filter((v) => v.status === s).length;
                    return (
                      <div key={s} className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`funnel-${s.toLowerCase()}`}>
                        <span className={`h-2.5 w-2.5 rounded-full ${VISITOR_STATUS_STYLES[s] || "bg-slate-500"}`} />
                        <span className="font-heading text-2xl font-extrabold text-slate-900">{count}</span>
                        <span className="text-xs text-slate-500">{t(`visitorStatus.${s}`)}</span>
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    className="ml-auto"
                    onClick={exportLeadsCsv}
                    data-testid="export-leads-csv"
                  >
                    <Download className="mr-1.5 h-4 w-4" /> {t("admin.exportCsv")}
                  </Button>
                </div>
                <div className="space-y-2">
                  {visitors.map((v) => (
                    <VisitorRow key={v.lead_id} lead={v} onChanged={loadAll} />
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          {/* Requirements */}
          <TabsContent value="funnels" className="mt-6">
            <FunnelLeadsTab />
          </TabsContent>

          <TabsContent value="stories" className="mt-6">
            <StoriesManager />
          </TabsContent>

          <TabsContent value="partners" className="mt-6">
            <PartnersManager />
          </TabsContent>

          <TabsContent value="integrations" className="mt-6">
            <IntegrationsPanel />
          </TabsContent>

          <TabsContent value="requirements" className="mt-6">
            {requirements.length === 0 ? (
              <p className="text-slate-400 py-10 text-center">{t("admin.noRequirements")}</p>
            ) : (
              <div className="space-y-3">
                {requirements.map((r) => {
                  const act = async (status) => {
                    try {
                      await api.patch(`/admin/requirements/${r.req_id}`, { status });
                      toast.success(status === "approved" ? t("admin.reqApproved") : t("admin.reqRejected"));
                      loadAll();
                    } catch (e) {
                      toast.error(t("admin.reqActionError"));
                    }
                  };
                  const badge = r.status === "approved" ? "bg-emerald-100 text-emerald-700" : r.status === "rejected" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700";
                  return (
                    <div key={r.req_id} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-requirement-row">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading text-lg font-bold text-slate-900">{r.designation}</h3>
                            <Badge className={`${badge} capitalize`}>{t(`requirement.status.${r.status}`)}</Badge>
                            <span className="text-xs text-slate-400">{r.source === "employer" ? "Employer" : "Public"}</span>
                          </div>
                          <p className="text-sm text-slate-500">{r.company_name}{r.contact_email ? ` · ${r.contact_email}` : ""}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" className="border-amber-300 text-amber-700" onClick={() => viewMatches(r)} data-testid={`req-matches-${r.req_id}`}>
                            <Sparkles className="mr-1.5 h-4 w-4" /> {t("admin.viewMatches")}
                          </Button>
                          {r.status === "pending" && (
                            <>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act("approved")} data-testid={`req-approve-${r.req_id}`}>
                                <CheckCircle2 className="mr-1.5 h-4 w-4" /> {t("admin.reqApprove")}
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => act("rejected")} data-testid={`req-reject-${r.req_id}`}>
                                <XCircle className="mr-1.5 h-4 w-4" /> {t("admin.reqReject")}
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      {r.skills?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {r.skills.map((s, i) => (
                            <span key={`${r.req_id}-${i}`} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{s}</span>
                          ))}
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-500">
                        <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-amber-600" /> {r.num_employees}</span>
                        {r.expected_date && <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-amber-600" /> {r.expected_date}</span>}
                        {r.offered_salary && <span className="flex items-center gap-1.5"><IndianRupee className="h-4 w-4 text-amber-600" /> {r.offered_salary}</span>}
                        {r.location && <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-amber-600" /> {r.location}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <Dialog open={!!matchDialog} onOpenChange={(o) => !o && setMatchDialog(null)}>
              <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg" data-testid="matches-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-amber-600" /> {t("admin.matchesFor")} {matchDialog?.designation}
                  </DialogTitle>
                  <DialogDescription>{t("admin.matchesDesc")}</DialogDescription>
                </DialogHeader>
                {matchLoading ? (
                  <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
                ) : matchList.length === 0 ? (
                  <p className="py-8 text-center text-slate-400" data-testid="no-matches">{t("admin.noMatches")}</p>
                ) : (
                  <div className="space-y-3">
                    {matchList.map((m) => (
                      <div key={m.user_id} className="rounded-xl border border-slate-200 p-4" data-testid="match-row">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{m.name}</p>
                            <p className="text-xs text-slate-500 truncate">{m.email}</p>
                          </div>
                          <Badge className="bg-amber-600 shrink-0">{t("admin.matchScore")}: {m.score}</Badge>
                        </div>
                        {m.matched_skills?.length > 0 && (
                          <p className="mt-2 text-xs text-slate-500"><span className="font-semibold">{t("admin.matchedSkills")}:</span> {m.matched_skills.join(", ")}</p>
                        )}
                        {m.matched_courses?.length > 0 && (
                          <p className="mt-1 text-xs text-slate-500"><span className="font-semibold">{t("admin.matchedCourses")}:</span> {m.matched_courses.join(", ")}</p>
                        )}
                        <div className="mt-3">
                          {shortlisted.includes(m.user_id) ? (
                            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600"><CheckCircle2 className="h-4 w-4" /> {t("admin.shortlistedDone")}</span>
                          ) : (
                            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => shortlistStudent(m.user_id)} data-testid={`shortlist-${m.user_id}`}>
                              <Mail className="mr-1.5 h-4 w-4" /> {t("admin.shortlistNotify")}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users */}
          <TabsContent value="users" className="mt-6">
            <Card className="border-slate-200 overflow-x-auto">
              <Table data-testid="admin-users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("admin.name")}</TableHead>
                    <TableHead>{t("admin.email")}</TableHead>
                    <TableHead>{t("admin.role")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.user_id} data-testid="admin-user-row">
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-slate-500">{u.email}</TableCell>
                      <TableCell>
                        <Badge className={u.role === "admin" ? "bg-indigo-600" : "bg-blue-600"}>{u.role}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function CourseForm({ onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [f, setF] = useState({ title: "", description: "", category: "", level: "Beginner", instructor: "", thumbnail: "", lessons: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    api.get("/admin/instructors").then(({ data }) => setInstructors(data)).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      const lessons = f.lessons.split("\n").map((l) => l.trim()).filter(Boolean).map((title) => ({ title, duration: "10 min" }));
      await api.post("/admin/courses", { ...f, lessons });
      toast.success(t("admin.courseCreated"));
      setOpen(false);
      setF({ title: "", description: "", category: "", level: "Beginner", instructor: "", thumbnail: "", lessons: "" });
      onCreated();
    } catch (e) {
      toast.error(t("admin.courseCreateError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700" data-testid="admin-create-course-btn">
          <Plus className="mr-1.5 h-4 w-4" /> {t("admin.newCourse")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="course-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("admin.createCourse")}</DialogTitle>
          <DialogDescription>{t("admin.createCourseDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("admin.fTitle")}</Label><Input data-testid="course-title-input" value={f.title} onChange={set("title")} className="mt-1" /></div>
          <div><Label>{t("admin.fDescription")}</Label><Textarea value={f.description} onChange={set("description")} rows={2} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("admin.fCategory")}</Label><Input value={f.category} onChange={set("category")} placeholder="Engineering" className="mt-1" /></div>
            <div><Label>{t("admin.fLevel")}</Label><Input value={f.level} onChange={set("level")} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("admin.fInstructor")}</Label>
              {instructors.length > 0 ? (
                <Select value={f.instructor} onValueChange={(v) => setF({ ...f, instructor: v })}>
                  <SelectTrigger className="mt-1" data-testid="course-instructor-select">
                    <SelectValue placeholder={t("admin.fInstructorPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {instructors.map((x) => (
                      <SelectItem key={x.expert_id} value={x.name}>
                        {x.name} — {x.expertise}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={f.instructor} onChange={set("instructor")} className="mt-1" placeholder={t("admin.fInstructorName")} />
              )}
            </div>
            <div><Label>{t("admin.fThumbnail")}</Label><Input value={f.thumbnail} onChange={set("thumbnail")} placeholder="https://…" className="mt-1" /></div>
          </div>
          <div><Label>{t("admin.fLessons")}</Label><Textarea value={f.lessons} onChange={set("lessons")} rows={4} placeholder={"Intro\nGetting started\n…"} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy || !f.title} className="bg-blue-600 hover:bg-blue-700" data-testid="course-form-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("admin.create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuizForm({ course, onSaved }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [questions, setQuestions] = useState([]);

  const openDialog = async () => {
    try {
      const { data } = await api.get(`/admin/courses/${course.course_id}`);
      setQuestions((data.quiz || []).map((q) => ({ _k: newKey(), text: q.text, options: q.options, correct_index: q.correct_index })));
    } catch (e) {
      setQuestions([]);
    }
    setOpen(true);
  };
  const addQ = () => setQuestions([...questions, { _k: newKey(), text: "", options: ["", "", "", ""], correct_index: 0 }]);
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const upd = (fn) => { const n = [...questions]; fn(n); setQuestions(n); };

  const save = async () => {
    setBusy(true);
    try {
      const clean = questions.filter((q) => q.text.trim() && q.options.every((o) => o.trim())).map(({ _k, ...q }) => q);
      await api.put(`/admin/courses/${course.course_id}/quiz`, { questions: clean });
      toast.success(t("admin.quizSaved"));
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(t("admin.quizSaveError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="ghost"
        size="icon"
        onClick={openDialog}
        className="text-slate-500 hover:text-blue-600 hover:bg-blue-50"
        data-testid="manage-quiz-button"
        title={t("admin.saveQuiz")}
      >
        <ListChecks className="h-4 w-4" />
      </Button>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg" data-testid="quiz-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("admin.quizFor", { title: course.title })}</DialogTitle>
          <DialogDescription>{t("admin.quizDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {questions.map((q, i) => (
            <div key={q._k} className="rounded-xl border border-slate-200 p-4" data-testid="quiz-builder-question">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-400">{t("admin.question")} {i + 1}</span>
                <button onClick={() => removeQ(i)} className="text-xs text-red-500 hover:underline" data-testid={`quiz-remove-${i}`}>
                  {t("admin.remove")}
                </button>
              </div>
              <Input
                value={q.text}
                onChange={(e) => upd((n) => (n[i].text = e.target.value))}
                placeholder={t("admin.questionText")}
                data-testid={`quiz-q-text-${i}`}
              />
              <div className="mt-3 space-y-2">
                {q.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <button
                      onClick={() => upd((n) => (n[i].correct_index = oi))}
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${
                        q.correct_index === oi ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-slate-500"
                      }`}
                      title="Mark as correct answer"
                      data-testid={`quiz-correct-${i}-${oi}`}
                    >
                      {String.fromCharCode(65 + oi)}
                    </button>
                    <Input
                      value={o}
                      onChange={(e) => upd((n) => (n[i].options[oi] = e.target.value))}
                      placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                    />
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-400">{t("admin.markCorrectHint")}</p>
            </div>
          ))}
          <Button variant="outline" onClick={addQ} data-testid="quiz-add-question">
            <Plus className="mr-1.5 h-4 w-4" /> {t("admin.addQuestion")}
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="quiz-save">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("admin.saveQuiz")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JobForm({ onCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ title: "", company: "", location: "", type: "Full-time", salary: "", description: "", requirements: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const requirements = f.requirements.split("\n").map((r) => r.trim()).filter(Boolean);
      await api.post("/admin/jobs", { ...f, requirements });
      toast.success(t("admin.jobPosted"));
      setOpen(false);
      setF({ title: "", company: "", location: "", type: "Full-time", salary: "", description: "", requirements: "" });
      onCreated();
    } catch (e) {
      toast.error(t("admin.jobPostError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700" data-testid="admin-create-job-btn">
          <Plus className="mr-1.5 h-4 w-4" /> {t("admin.newJob")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="job-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("admin.postJob")}</DialogTitle>
          <DialogDescription>{t("admin.postJobDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>{t("admin.fTitle")}</Label><Input data-testid="job-title-input" value={f.title} onChange={set("title")} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("admin.fCompany")}</Label><Input value={f.company} onChange={set("company")} className="mt-1" /></div>
            <div><Label>{t("admin.fLocation")}</Label><Input value={f.location} onChange={set("location")} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t("admin.fType")}</Label>
              <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}>
                <SelectTrigger className="mt-1" data-testid="job-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JOB_TYPES.map((jt) => <SelectItem key={jt} value={jt}>{jt}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t("admin.fSalary")}</Label><Input value={f.salary} onChange={set("salary")} className="mt-1" /></div>
          </div>
          <div><Label>{t("admin.fDescription")}</Label><Textarea value={f.description} onChange={set("description")} rows={2} className="mt-1" /></div>
          <div><Label>{t("admin.fRequirements")}</Label><Textarea value={f.requirements} onChange={set("requirements")} rows={3} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={busy || !f.title} className="bg-blue-600 hover:bg-blue-700" data-testid="job-form-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("admin.postJobBtn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
